import { Category } from '@/types';
import {
  saveCategoryToSupabase,
  deleteCategoryFromSupabase,
  loadCategoriesFromSupabase,
  checkCategoriesInitialized,
  createDefaultCategories,
} from '@/lib/supabase-categories-operations';
import { logger } from '@/utils/logger';

export interface LoadResult {
  categories: Category[];
  profileKey: string;
}

export const loadCategoriesForProfile = async (
  profileType: 'business' | 'personal',
  profileId: string
): Promise<LoadResult> => {
  const profileKey = profileId || 'default';
  
  logger.info('[Categories] Loading categories', { profileKey });
  
  const isInitialized = await checkCategoriesInitialized();
  
  let categories: Category[];
  
  if (!isInitialized) {
    logger.info('[Categories] Creating default categories', { profileType });
    categories = await createDefaultCategories(profileType);
  } else {
    categories = await loadCategoriesFromSupabase();
  }
  
  return { categories, profileKey };
};

export const addCategory = async (
  categoryData: Omit<Category, 'id'>
): Promise<Category> => {
  const newCategory: Category = {
    ...categoryData,
    id: crypto.randomUUID(),
  };

  logger.info('[Categories] Adding category', { name: newCategory.name });
  await saveCategoryToSupabase(newCategory);
  logger.info('[Categories] Category added', { id: newCategory.id });
  
  return newCategory;
};

export const updateCategory = async (
  id: string,
  updates: Partial<Category>,
  currentCategory: Category
): Promise<Category> => {
  const updatedCategory = { ...currentCategory, ...updates };
  
  logger.info('[Categories] Updating category', { id });
  await saveCategoryToSupabase(updatedCategory);
  logger.info('[Categories] Category updated', { id });
  
  return updatedCategory;
};

export const deleteCategory = async (
  id: string,
  category: Category | undefined
): Promise<void> => {
  if (category?.isDefault) {
    throw new Error('Cannot delete default category');
  }

  logger.info('[Categories] Deleting category', { id });
  await deleteCategoryFromSupabase(id);
  logger.info('[Categories] Category deleted', { id });
};
